import { useEffect, useRef, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { LoaderCircle, Maximize2, Minimize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { components } from '@/types/api.generated';
import { ErrorState } from '@resource-kit/ui/react';
import { useToday } from '@resource-kit/controllers/react';
import type { RoutePlanningStop } from '../map/RoutePlanningMap';
import { LazyRoutePlanningMap } from '../map/LazyRoutePlanningMap';
import { useRouteMonitoringJoin } from '../hooks/useRouteMonitoringJoin';
import { useRoutePlanningStops } from '../hooks/useRoutePlanningStops';
import { RouteSidebar } from './RouteSidebar';

type DispatchRouteEtaReport = components['schemas']['DispatchRouteEtaReport'];

export interface RoutePlanningSectionProps {
  date: string;
  eta?: UseQueryResult<DispatchRouteEtaReport>;
}

export function RoutePlanningSection({ date, eta }: RoutePlanningSectionProps) {
  const { t } = useTranslation();
  const [selectedStop, setSelectedStop] = useState<RoutePlanningStop | null>(null);
  // This section owns "which driver am I looking at": the sidebar reports the
  // intent and the map obeys it, so neither half can drift from the other.
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  // The native Fullscreen API's own `document.fullscreenElement` is the
  // single source of truth for "are we fullscreen" — this flag only mirrors
  // it via `fullscreenchange`, so a browser-native exit gesture (Esc, the
  // browser's own chrome) stays in sync without the component guessing.
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  // Fallback for browsers without `Element.prototype.requestFullscreen`: a
  // CSS overlay driven entirely by component state, no native event to sync.
  const [isOverlayFullscreen, setIsOverlayFullscreen] = useState(false);
  const isFullscreen = isNativeFullscreen || isOverlayFullscreen;

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsNativeFullscreen(document.fullscreenElement === rootRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement === rootRef.current) {
      void document.exitFullscreen();
      return;
    }
    if (isOverlayFullscreen) {
      setIsOverlayFullscreen(false);
      return;
    }
    const root = rootRef.current;
    if (root && typeof root.requestFullscreen === 'function') {
      // A rejected request (e.g. the tab lost permission mid-gesture) still
      // needs a way in, so fall back to the overlay rather than doing
      // nothing — this is a fallback path, not a swallowed error.
      root.requestFullscreen().catch(() => setIsOverlayFullscreen(true));
      return;
    }
    setIsOverlayFullscreen(true);
  };

  const clearDriverSelection = () => {
    setSelectedDriverId(null);
    setSelectedStop(null);
  };

  const toggleDriverSelection = (workerId: string) => {
    setSelectedDriverId((previous) => (previous === workerId ? null : workerId));
    // The popup belongs to the stop of whoever was selected a moment ago, so
    // it must not be left hanging over a route that has just been dimmed.
    setSelectedStop(null);
  };

  const focusStop = (stop: RoutePlanningStop, driverId?: string) => {
    setSelectedStop(stop);
    // Looking at one driver's stop is looking at that driver's route.
    if (driverId !== undefined) {
      setSelectedDriverId(driverId);
    }
  };

  // Escape is the way out of a selection that does not require finding a
  // control first — unless fullscreen is open, in which case Escape's job is
  // leaving fullscreen. A native fullscreen session usually eats Escape
  // before this handler ever sees it, but that cannot be relied on: the
  // overlay fallback gets no such help, so the check has to happen here too.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Escape inside an open dialog (e.g. the Cmd/Ctrl+K palette) only closes it (#2477).
      if (event.target instanceof Element && event.target.closest('[role="dialog"]')) return;
      if (isFullscreen) {
        if (document.fullscreenElement) {
          void document.exitFullscreen();
        } else {
          setIsOverlayFullscreen(false);
        }
        return;
      }
      setSelectedDriverId(null);
      setSelectedStop(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFullscreen]);

  const stopsQuery = useRoutePlanningStops(date);

  const today = useToday();
  const isToday = date === today;

  const monitoringJoin = useRouteMonitoringJoin({
    stops: stopsQuery.data?.stops ?? [],
    etaDrivers: isToday ? eta?.data?.drivers : undefined,
    isToday,
  });

  // A driver who goes off duty or finishes his last stop drops out of the
  // join. The selection has to leave with him: his route is gone, so nothing
  // would be highlighted and the chip that clears the selection — the way back
  // out — would be gone too.
  useEffect(() => {
    if (selectedDriverId === null) return;
    const stillOnTheRoad = monitoringJoin.drivers.some(
      (driver) => driver.workerId === selectedDriverId,
    );
    if (!stillOnTheRoad) {
      setSelectedDriverId(null);
      setSelectedStop(null);
    }
  }, [selectedDriverId, monitoringJoin]);

  if (stopsQuery.isLoading) {
    return (
      <div
        role="status"
        className="flex min-h-96 items-center justify-center gap-2 text-sm text-muted-foreground"
      >
        <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
        {t('routePlanning.loading')}
      </div>
    );
  }

  if (stopsQuery.isError || stopsQuery.data === undefined) {
    return <ErrorState message={t('routePlanning.error')} className="min-h-96" />;
  }

  const { stops, missing } = stopsQuery.data;
  const selectedDriverName =
    monitoringJoin.drivers.find((driver) => driver.workerId === selectedDriverId)?.workerName ??
    null;

  return (
    <div
      ref={rootRef}
      // In fullscreen, below `lg` the two halves stack instead of sitting side
      // by side and their own minimum heights already exceed `h-screen`, so the
      // root has to scroll — otherwise the sidebar is clipped off the bottom
      // with no way to reach it.
      className={
        isFullscreen
          ? 'grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] fixed inset-0 z-50 h-screen overflow-auto bg-background p-4'
          : 'grid min-h-[36rem] gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] lg:h-[calc(100vh-10rem)]'
      }
    >
      {/* Left: Map */}
      <section className="flex min-h-[32rem] min-w-0 flex-col overflow-hidden rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b px-4 py-3 text-sm text-muted-foreground">
          <span>{t('routePlanning.stopCount', { count: stops.length })}</span>
          <div className="flex items-center gap-3">
            {/* A failed ETA query has two shapes, and neither one the map can
                express. Never answered: it paints a neutral dot (#2261), and a
                dot cannot say WHY. Answered once and then failing: TanStack
                keeps `data`, so it keeps painting the last frame's
                assignments, which look current. Either way the failure is
                indistinguishable from a quiet day, so it is said in words
                where the live-monitoring chip would otherwise be — worded for
                the stale case, which is the weaker claim of the two. */}
            {isToday && eta?.isError === true && (
              <span role="status" className="text-xs font-medium text-warning">
                {t('routePlanning.etaError')}
              </span>
            )}
            {isToday && monitoringJoin.drivers.length > 0 && (
              <span className="flex items-center gap-1.5 text-xs text-foreground font-medium">
                <span className="h-2 w-2 rounded-full bg-success inline-block animate-pulse" />
                {t('routePlanning.liveMonitoringActive', { count: monitoringJoin.drivers.length })}
              </span>
            )}
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label={
                isFullscreen
                  ? t('routePlanning.fullscreen.exit')
                  : t('routePlanning.fullscreen.enter')
              }
              className="cursor-pointer rounded p-1 text-muted-foreground hover:bg-accent/50 hover:text-foreground"
            >
              {isFullscreen ? (
                <Minimize2 className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Maximize2 className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
        <div className="relative min-h-0 flex-1">
          <LazyRoutePlanningMap
            stops={stops}
            monitoringJoin={monitoringJoin}
            focusedStop={selectedStop}
            selectedDriverId={selectedDriverId}
            className="h-full rounded-none"
          />
          {/* The chip sits on the basemap, which stays light in either theme,
              so it sits in the theme-light scope (src/index.css), which re-binds the light tokens. */}
          {selectedDriverName !== null && (
            <div className="theme-light absolute left-3 top-3 z-10 flex items-center gap-2 rounded-md bg-card/90 px-2.5 py-1.5 text-xs font-medium text-foreground shadow">
              <span>{t('routePlanning.driver.onlyDriver', { name: selectedDriverName })}</span>
              <button
                type="button"
                onClick={clearDriverSelection}
                className="cursor-pointer rounded px-1.5 py-0.5 font-medium text-muted-foreground underline-offset-2 hover:underline"
              >
                {t('routePlanning.driver.showAll')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Right: Sidebar */}
      <RouteSidebar
        monitoringJoin={monitoringJoin}
        missing={missing}
        isToday={isToday}
        selectedStop={selectedStop}
        selectedDriverId={selectedDriverId}
        onSelectDriver={toggleDriverSelection}
        onSelectStop={focusStop}
        className="h-full min-h-[24rem]"
      />
    </div>
  );
}
